/**
 * Todo el copy del alta pública, en un solo lugar.
 *
 * Junto y no repartido por los componentes para que se pueda **leer entero de
 * una sentada**: el tono de una guía se revisa completo o no se revisa. Cazando
 * strings por seis archivos nadie nota que un paso trata al dueño de usted y el
 * siguiente de vos.
 *
 * Dos reglas que el test verifica:
 *
 * - **Español, sin jerga** (regla D6). Quien lee esto es el dueño de una
 *   veterinaria, no nosotros: no sabe qué es una KB, un ERP ni un webhook.
 * - **Los botones dicen lo que hacen.** Nada de «Continuar» en un botón que
 *   además guarda: cuando el guardado falla, nadie sabe si quedó o no.
 */

import { entrevista } from './textos-entrevista';

import { identidad } from './textos-identidad';
import { listo } from './textos-listo';
import { noSePudoEmpezar, porQueNoSePudo, web } from './textos-web';

export const textos = {
  listo,
  botones: {
    guardarYSeguir: 'Guardar y seguir',
    guardarYSalir: 'Guardar y salir',
    guardando: 'Guardando…',
    reintentar: 'Reintentar',
  },

  registro: {
    titulo: 'Creá tu bot',
    bajada: 'En unos minutos tenés un asistente contestando por vos.',

    negocio: '¿Cómo se llama tu negocio?',
    correo: 'Tu correo',
    contrasena: 'Una contraseña',
    ayudaContrasena: 'Al menos 8 caracteres.',

    crear: 'Crear mi cuenta',
    creando: 'Creando tu cuenta…',

    // Lo que evita que se quede mirando una pantalla que no cambió. Es el punto
    // exacto donde se abandona un registro.
    listoTitulo: 'Revisá tu correo',
    // Decía «cuando lo confirmes, entrá» y no había dónde: ahora el código se
    // pega acá mismo, sin cambiar de pantalla ni volver después.
    listoTexto: 'Te mandamos un código a tu correo. Pegalo acá y seguimos.',

    codigoEtiqueta: 'El código que te llegó',
    codigoConfirmar: 'Confirmar y seguir',
    codigoConfirmando: 'Confirmando…',
    // Si el correo no llegó, decirlo antes de que lo pregunte.
    codigoNoLlego: 'Si no te llegó, fijate en correo no deseado.',

    yaTengoCuenta: 'Ya tengo cuenta, quiero iniciar sesión',

    claveCorta: 'La contraseña necesita al menos 8 caracteres.',
    noSePudo: 'No pudimos crear tu cuenta. Revisá los datos y probá de nuevo.',
  },

  cargando: 'Buscando dónde quedaste…',

  errores: {
    sinNegocio: 'Tu cuenta todavía no tiene un negocio asociado. Escribinos y lo resolvemos.',
    noSePudoLeer: 'No pudimos ver cómo va tu configuración. Probá de nuevo en un momento.',
    // Dice qué pasó y qué hacer. «Ocurrió un error» no es ninguna de las dos.
    noSeGuardo: 'No pudimos guardar los cambios. Revisá tu conexión y probá de nuevo.',
    // Lo importante: que sepa que NO perdió lo que escribió.
    tranquilidad: 'Lo que escribiste sigue acá.',
  },

  canales: {
    titulo: '¿Por dónde te escriben tus clientes?',
    // El alivio va primero: la pantalla muestra dos tarjetas y hay que decir de
    // entrada que no hacen falta las dos.
    ayuda: 'Con conectar uno alcanza para empezar. Después podés sumar el otro.',

    telegram: {
      nombre: 'Telegram',
      // Va primero porque el dueño puede hacerlo solo en dos minutos.
      promesa: 'Lo conectás vos en dos minutos.',
      comoSeHace: 'Necesitás el token que te da @BotFather en Telegram.',
    },

    whatsapp: {
      nombre: 'WhatsApp',
      // ⚠️ Dice la verdad a propósito: hasta que exista Embedded Signup
      // (GKTIBO-18) la conexión la hacemos nosotros a mano. Prometer
      // autoservicio acá es la misma falla que GKTIBO-44 un escalón más arriba:
      // ofrecer una capacidad que no tenemos.
      promesa: 'Lo conectamos nosotros por vos.',
      comoSeHace:
        'Escribinos cuando lo quieras conectar y lo dejamos andando. Suele tomar un día hábil.',
      // Por qué esta tarjeta NO muestra formulario: los datos que pide
      // WhatsApp —«Phone Number ID», «Access Token», «App Secret»— son de la
      // consola de desarrollador de Meta. Un dueño no los tiene ni sabe qué
      // son, y pedírselos contradice la promesa de arriba.
      comoPedirlo: 'Escribinos y te lo dejamos conectado.',
    },

    conectado: 'Conectado',
    sinConectar: 'Sin conectar',
    // NO dice «te falta»: el segundo canal es una posibilidad, no una deuda.
    disponible: 'Podés sumarlo cuando quieras',
  },

  /** El indicador de arriba: dónde estoy y cuánto falta. */
  pasos: {
    titulo: 'Pasos del alta',
    nombres: {
      identidad: 'Tu negocio',
      conocimiento: 'Lo que tu bot sabe',
      listo: 'Listo',
    } as const,
    // Decir el total es la mitad del valor: «paso 2» solo no dice si falta uno
    // o diez.
    cuenta: (actual: number, total: number) => `Paso ${actual} de ${total}`,
  },

  identidad,

  conocimiento: {
    titulo: 'Contale a tu bot sobre tu negocio',
    // El gate del paso 2 (GKTIBO-123). Se dice como invitación, no como
    // reproche: probarlo es la parte que al dueño le sirve a él.
    probaloPrimero:
      'Probá tu bot al menos una vez antes de seguir. Escribile como lo haría un cliente.',
    yaProbaste: 'Ya lo probaste. Podés seguir probando o continuar.',
    continuar: 'Continuar',
    noSePudoSeguir: 'No pudimos guardar que ya probaste —es un problema nuestro—. Probá de nuevo.',

    // Lo primero es la consecuencia: sin esto no puede salir en vivo. Que lo
    // descubra recién al llegar al botón gris es hacerle perder el viaje.
    porQue:
      'Lo que escribas acá es todo lo que tu bot va a saber. Sin esto no puede salir en vivo, ' +
      'porque un bot que no sabe nada de tu negocio te deja mal delante de tus clientes.',

    // Y lo segundo, lo que ninguna página web responde. Es lo que evita que el
    // bot prometa cosas que el negocio no puede cumplir.
    loQueMasImporta:
      'Lo más valioso son dos cosas que no están escritas en ningún lado: qué NO hacés, ' +
      'y cuándo tu bot tiene que dejar de responder y avisarte.',

    dejamosEmpezado: 'Te dejamos un borrador con ejemplos. Contale lo que falte.',

    entrevista,

    /**
     * El editor del `.md`, plegado.
     *
     * El alta no tiene editor de Markdown en ninguna pantalla, y desde el
     * 2026-09-19 tampoco lo tiene plegado: se fue. Frontmatter, `## Identity` y
     * «6 chunks» en el primer paso útil es donde se abandona un alta.
     */
    /**
     * El panel de lo que el bot sabe (GKTIBO-69).
     *
     * **Sólo lectura.** El dueño no es técnico y no tiene por qué tocar el
     * archivo: «Cambiar» no abre una caja de texto, le habla al asistente.
     * Decisión del PO, 2026-09-19.
     */
    panel: {
      titulo: 'Lo que tu bot sabe',
      bajada: 'Esto es todo lo que va a poder contestar.',
      // Nunca «sección», «markdown» ni «base de conocimiento» (D6).
      cambiar: 'Cambiar',
      // Sin etiqueta de origen: hoy nadie guarda quién escribió cada tema, y
      // afirmarlo sería inventar. Vuelve con GKTIBO-88.
      falta: 'falta',
      opcional: 'opcional',
      // Sin esto una conversación no dice cuánto queda, y no saberlo es el
      // momento exacto en que alguien abandona.
      cuantoFalta: (n: number) =>
        n === 1
          ? 'Falta un tema para que tu bot pueda salir en vivo.'
          : `Faltan ${n} temas para que tu bot pueda salir en vivo.`,
      listo: 'Tu bot ya sabe lo que necesita para atender.',
      // Todavía no hay nada cargado. No es un error: es el estado de quien
      // recién se registró.
      vacio: 'Todavía no le contaste nada a tu bot. Empecemos.',
      noSePudoLeer: 'No pudimos mostrarte esto ahora. Volvé a intentar en un minuto.',
    },

    /**
     * La sección de la página web: en `textos-web.ts` (GKTIBO-136, D4).
     * Sigue colgando de acá para que quien lee el copy del alta lo encuentre
     * entero de una sentada.
     */
    web,
    porQueNoSePudo,
    noSePudoEmpezar,
  },

  revision: {
    titulo: 'Ya casi',
    listo: 'Tu bot está listo para atender.',
    faltan: 'Antes de poner tu bot en vivo:',

    activar: 'Poner mi bot en vivo',
    activando: 'Activando…',

    // La confirmación dice QUÉ VA A PASAR, no «¿estás seguro?». Es el momento
    // en que un desconocido puede escribirle al negocio y contestarle una
    // máquina: merece decirse con esas palabras.
    confirmarTitulo: '¿Ponemos tu bot en vivo?',
    confirmarQuePasa:
      'Desde ese momento, quien te escriba por el canal que conectaste va a hablar con tu bot.',
    confirmarSiga: 'Sí, ponerlo en vivo',
    confirmarVuelva: 'Todavía no',

    // Se dice ANTES de que se acabe. Que el bot deje de responder sin haberlo
    // avisado es la peor primera impresión posible.
    cuotaDeLaPrueba: 'Tu prueba incluye 1.000 mensajes. Te avisamos antes de que se acaben.',

    // El servidor puede decir «todavía no» por una razón que el panel no sabe
    // derivar — hoy, por ejemplo, cuando no pudo revisar el conocimiento. Un
    // botón gris sin explicación es peor que una explicación imperfecta.
    razonDesconocida:
      'Estamos terminando de revisar tu configuración. Probá de nuevo en un momento.',
    noSePudoRevisar: 'No pudimos revisar lo que cargaste. Probá de nuevo en un momento.',

    enVivo: 'Tu bot está en vivo.',
    noSePudoActivar: 'No pudimos activar tu bot. Probá de nuevo en un momento.',
  },
} as const;
